import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { makeResource } from '@/lib/api/resources';
import { resolveI18n } from '@/lib/i18n/content';
import { useLocale } from '@/lib/i18n/useLocale';
import type { I18n } from '@/lib/api/types';
import { Select } from '@/lib/ui/Select';

/**
 * A reference stored as a *slug* (not a UUID): a project's `industry`, a product's `family`,
 * an industry's related `project_slugs`. The editor picks a human name from a dropdown and we
 * persist the canonical slug — so the public facet filters match (contract §6) and a
 * non-technical editor never sees a raw UUID. Options come live from the referenced
 * collection, so adding a new industry/family needs no code change.
 */
export interface RefItem {
  id: string;
  slug?: string;
  key?: string;
  name?: I18n;
  title?: I18n;
}

const REF_BASE: Record<string, string> = {
  'product-families': '/admin/product-families',
  industries: '/admin/industries',
  projects: '/admin/projects',
  articles: '/admin/articles',
  'job-postings': '/admin/job-postings',
  products: '/admin/products',
};

export function useReferenceItems(collection: string) {
  const base = REF_BASE[collection] ?? `/admin/${collection}`;
  return useQuery({
    queryKey: ['ref-options', collection],
    queryFn: () => makeResource<RefItem>(base).list({ per_page: 100, locale: 'all' }),
    staleTime: 60_000,
  });
}

function refLabel(item: RefItem, locale: 'en' | 'ar'): string {
  return resolveI18n(item.name ?? item.title, locale) || item.slug || item.key || item.id;
}

export function ReferenceSelectField({
  label,
  collection,
  value,
  onChange,
  required,
  error,
  disabled,
  hint,
}: {
  label: string;
  collection: string;
  value: string | null | undefined;
  /** Emits the chosen slug (or null), plus the resolved item for sibling id sync. */
  onChange: (slug: string | null, item?: RefItem) => void;
  required?: boolean;
  error?: string | null;
  disabled?: boolean;
  hint?: string;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const query = useReferenceItems(collection);

  const options = useMemo(() => {
    const items = query.data?.data ?? [];
    const opts = items
      .filter((i) => i.slug)
      .map((i) => ({ value: i.slug!, label: refLabel(i, locale) }))
      .sort((a, b) => a.label.localeCompare(b.label));
    // Keep a dangling value (e.g. a slug whose item is no longer listed) selectable so the
    // editor can see and clear it rather than silently losing the reference.
    if (value && !opts.some((o) => o.value === value)) {
      opts.unshift({ value, label: t('forms.refMissing', { value, defaultValue: `${value} (not found)` }) });
    }
    return [{ value: '', label: t('forms.refNone') }, ...opts];
  }, [query.data, locale, value, t]);

  const onSelect = (slug: string) => {
    if (!slug) return onChange(null);
    const item = (query.data?.data ?? []).find((i) => i.slug === slug);
    onChange(slug, item);
  };

  return (
    <div className="flex flex-col gap-[6px]">
      <Select
        label={t(label, { defaultValue: label })}
        value={value ?? ''}
        onValueChange={onSelect}
        options={options}
        required={required}
        disabled={disabled || query.isLoading}
        placeholder={query.isLoading ? t('common.loading') : t('forms.refNone')}
        error={error}
      />
      {hint && !error && <p className="text-small text-ink-mute">{t(hint, { defaultValue: hint })}</p>}
    </div>
  );
}
